import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import Logo from "../../../../assets/img/Dwelly_FullLogo_Transparent.png";
import moment from "moment";
import FontVerdana from "../../../../assets/fonts/Verdana.ttf";
import FontVerdanaBold from "../../../../assets/fonts/Verdana Bold.ttf";
import FontVerdanaItalic from "../../../../assets/fonts/Verdana Italic.ttf";
import FontVerdanaBoldItalic from "../../../../assets/fonts/Verdana Bold Italic.ttf";
import { CurrencyFormatter, FormatCurrencyPlain, round2 } from "./trustReconciliationUtils";

// Register Verdana font safely
try {
  Font.register({
    family: "Verdana",
    fonts: [
      { src: FontVerdana },
      { src: FontVerdanaBold, fontWeight: "bold" },
      { src: FontVerdanaItalic, fontStyle: "italic" },
      { src: FontVerdanaBoldItalic, fontWeight: "bold", fontStyle: "italic" },
    ],
  });
} catch (e) {
  // If already registered, ignore
}

const styles = StyleSheet.create({
  page: {
    fontFamily: "Verdana",
    fontSize: 8,
    color: "#2D3748",
    paddingTop: 56,
    paddingBottom: 70,
    paddingHorizontal: 64,
    backgroundColor: "#FFFFFF",
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottomWidth: 1.5,
    borderBottomColor: "#CBD5E0",
    paddingBottom: 10,
    marginBottom: 10,
  },
  logo: {
    width: 125,
    height: "auto",
    marginBottom: 3,
  },
  companyInfo: {
    fontSize: 7,
    color: "#4A5568",
    lineHeight: 1.25,
  },
  titleBlock: {
    alignItems: "flex-end",
    maxWidth: "58%",
  },
  accountBadge: {
    backgroundColor: "#E6FFFA",
    color: "#234E52",
    fontSize: 7.5,
    fontWeight: "bold",
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
    marginBottom: 3,
    borderWidth: 0.5,
    borderColor: "#319795",
  },
  docTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#1A202C",
    textAlign: "right",
    marginBottom: 2,
  },
  docSubtitle: {
    fontSize: 7,
    color: "#2C7A7B",
    fontWeight: "bold",
    textAlign: "right",
    marginBottom: 3,
  },
  metaText: {
    fontSize: 7,
    color: "#4A5568",
    textAlign: "right",
    lineHeight: 1.25,
  },
  sectionTitle: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: "#1A202C",
    backgroundColor: "#EDF2F7",
    paddingVertical: 3.5,
    paddingHorizontal: 6,
    marginTop: 8,
    marginBottom: 5,
    borderRadius: 2,
  },
  table: {
    width: "100%",
    marginBottom: 8,
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#F7FAFC",
    borderBottomWidth: 1,
    borderBottomColor: "#CBD5E0",
    paddingVertical: 3.5,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#E2E8F0",
    paddingVertical: 3,
    paddingHorizontal: 4,
    minHeight: 13,
    alignItems: "center",
  },
  tableTotalRow: {
    flexDirection: "row",
    backgroundColor: "#EDF2F7",
    borderTopWidth: 1,
    borderTopColor: "#A0AEC0",
    borderBottomWidth: 1,
    borderBottomColor: "#A0AEC0",
    paddingVertical: 3.5,
    paddingHorizontal: 4,
    fontWeight: "bold",
  },
  thText: {
    fontSize: 7,
    fontWeight: "bold",
    color: "#2D3748",
  },
  tdText: {
    fontSize: 6.5,
    color: "#2D3748",
  },
  tdTextBold: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: "#1A202C",
  },
  textRight: {
    textAlign: "right",
  },
  textCenter: {
    textAlign: "center",
  },
  textLeft: {
    textAlign: "left",
  },
  threeWayBox: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderWidth: 1,
    borderColor: "#CBD5E0",
    borderRadius: 4,
    padding: 7,
    backgroundColor: "#F7FAFC",
    marginBottom: 8,
  },
  threeWayCol: {
    width: "31%",
    flexDirection: "column",
  },
  threeWayColTitle: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: "#2C7A7B",
    borderBottomWidth: 1,
    borderBottomColor: "#CBD5E0",
    paddingBottom: 2,
    marginBottom: 3,
  },
  threeWayItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 6.5,
    marginVertical: 1,
  },
  badgeSuccess: {
    backgroundColor: "#C6F6D5",
    color: "#22543D",
    fontSize: 7,
    fontWeight: "bold",
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 3,
    textAlign: "center",
    marginTop: 3,
  },
  signatureSection: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#CBD5E0",
    paddingTop: 8,
  },
  certText: {
    fontSize: 6.5,
    fontStyle: "italic",
    color: "#4A5568",
    lineHeight: 1.3,
    marginBottom: 10,
  },
  signatureGrid: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 6,
  },
  sigBox: {
    width: "46%",
  },
  sigLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#4A5568",
    height: 20,
    marginBottom: 3,
  },
  sigLabel: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: "#2D3748",
  },
  sigSub: {
    fontSize: 6,
    color: "#718096",
  },
  footerNotice: {
    marginTop: 10,
    paddingTop: 4,
    borderTopWidth: 0.5,
    borderTopColor: "#E2E8F0",
    fontSize: 5.5,
    color: "#A0AEC0",
    textAlign: "center",
  },
});

/**
 * Single Trust Account Reconciliation Page
 */
const TrustAccountPage = ({
  accountData,
  periodLabel,
  periodEndFormatted,
  dueDateFormatted,
  accountTitle,
  liabilityTitle,
  liabilityHeaderCol,
}) => {
  const { openingBalance, totalDebits, totalCredits, netMonthlyActivity, closingBalance, threeWayReconciliation, trustLiabilityList } = accountData;
  const activity = netMonthlyActivity !== undefined ? netMonthlyActivity : round2(closingBalance - openingBalance);

  return (
    <Page size="LETTER" style={styles.page}>
      {/* Header */}
      <View style={styles.headerRow}>
        <View style={{ width: "42%" }}>
          <Image style={styles.logo} src={Logo} />
          <Text style={{ ...styles.companyInfo, fontWeight: "bold", color: "#1A202C" }}>
            Dwelly Realty Inc., Brokerage
          </Text>
          <Text style={styles.companyInfo}>
            200-55 Village Centre Pl, Mississauga, ON L4Z 1V9 CA
          </Text>
          <Text style={styles.companyInfo}>
            Tel: 647.797.1133  |  Email: deals@dwellyrealty.com
          </Text>
        </View>

        <View style={styles.titleBlock}>
          <Text style={styles.accountBadge}>{accountTitle.toUpperCase()}</Text>
          <Text style={styles.docTitle}>MONTHLY TRUST RECONCILIATION</Text>
          <Text style={styles.docSubtitle}>
            Section 13 of O. Reg. 579/05 (TRESA)
          </Text>
          <Text style={styles.metaText}>
            <Text style={{ fontWeight: "bold" }}>Period Ended: </Text>
            {periodEndFormatted}
          </Text>
          <Text style={styles.metaText}>
            <Text style={{ fontWeight: "bold" }}>Statutory Due Date: </Text>
            {dueDateFormatted}
          </Text>
        </View>
      </View>

      {/* Account Movement Summary */}
      <Text style={styles.sectionTitle}>
        1. TRUST ACCOUNT STATEMENT & CASH ACTIVITY
      </Text>
      <View style={styles.table}>
        <View style={styles.tableHeaderRow}>
          <Text style={{ ...styles.thText, width: "34%" }}>Designated Trust Account</Text>
          <Text style={{ ...styles.thText, width: "16%", ...styles.textRight }}>Opening Balance</Text>
          <Text style={{ ...styles.thText, width: "16%", ...styles.textRight }}>Receipts (Debits)</Text>
          <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>Disbursements (Credits)</Text>
          <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>Closing Book Balance</Text>
        </View>

        <View style={styles.tableTotalRow}>
          <Text style={{ ...styles.thText, width: "34%" }}>{accountData.accountName}</Text>
          <Text style={{ ...styles.thText, width: "16%", ...styles.textRight }}>{CurrencyFormatter(openingBalance)}</Text>
          <Text style={{ ...styles.thText, width: "16%", ...styles.textRight, color: "#22543D" }}>{CurrencyFormatter(totalDebits)}</Text>
          <Text style={{ ...styles.thText, width: "17%", ...styles.textRight, color: "#C53030" }}>{CurrencyFormatter(totalCredits)}</Text>
          <Text style={{ ...styles.thText, width: "17%", ...styles.textRight, color: "#234E52" }}>{CurrencyFormatter(closingBalance)}</Text>
        </View>
      </View>

      {/* Three-Way Reconciliation */}
      <Text style={styles.sectionTitle}>
        2. THREE-WAY TRUST ACCOUNT RECONCILIATION
      </Text>
      <View style={styles.threeWayBox}>
        {/* Col 1 */}
        <View style={styles.threeWayCol}>
          <Text style={styles.threeWayColTitle}>A. Bank Statement Balance</Text>
          <View style={styles.threeWayItem}>
            <Text style={{ width: "65%" }}>Statement Closing Balance:</Text>
            <Text style={{ fontWeight: "bold" }}>{CurrencyFormatter(threeWayReconciliation.bankStatementBalance)}</Text>
          </View>
          <View style={styles.threeWayItem}>
            <Text style={{ width: "65%" }}>Plus: Deposits in Transit:</Text>
            <Text>{CurrencyFormatter(threeWayReconciliation.outstandingDeposits)}</Text>
          </View>
          <View style={styles.threeWayItem}>
            <Text style={{ width: "65%" }}>Less: Outstanding Cheques:</Text>
            <Text>{CurrencyFormatter(threeWayReconciliation.outstandingDisbursements)}</Text>
          </View>
          <View style={{ ...styles.threeWayItem, borderTopWidth: 0.5, borderTopColor: "#CBD5E0", paddingTop: 2, marginTop: 2 }}>
            <Text style={{ fontWeight: "bold" }}>Reconciled Bank Balance:</Text>
            <Text style={{ fontWeight: "bold", color: "#2C7A7B" }}>{CurrencyFormatter(threeWayReconciliation.reconciledBankBalance)}</Text>
          </View>
        </View>

        {/* Col 2 */}
        <View style={styles.threeWayCol}>
          <Text style={styles.threeWayColTitle}>B. Trust Ledger / Book</Text>
          <View style={styles.threeWayItem}>
            <Text style={{ width: "60%" }}>Opening Trust Book:</Text>
            <Text>{CurrencyFormatter(openingBalance)}</Text>
          </View>
          <View style={styles.threeWayItem}>
            <Text style={{ width: "60%" }}>Net Monthly Activity:</Text>
            <Text>{CurrencyFormatter(activity)}</Text>
          </View>
          <View style={{ ...styles.threeWayItem, borderTopWidth: 0.5, borderTopColor: "#CBD5E0", paddingTop: 2, marginTop: 6 }}>
            <Text style={{ fontWeight: "bold" }}>Closing Book Balance:</Text>
            <Text style={{ fontWeight: "bold", color: "#2C7A7B" }}>{CurrencyFormatter(threeWayReconciliation.bookBalance)}</Text>
          </View>
        </View>

        {/* Col 3 */}
        <View style={styles.threeWayCol}>
          <Text style={styles.threeWayColTitle}>C. Client Trust Liability</Text>
          <View style={styles.threeWayItem}>
            <Text style={{ width: "60%" }}>{liabilityHeaderCol || "Trust Liability Listing:"}</Text>
            <Text style={{ fontWeight: "bold" }}>{CurrencyFormatter(threeWayReconciliation.totalTrustLiability)}</Text>
          </View>
          <View style={styles.threeWayItem}>
            <Text style={{ width: "60%" }}>Reconciliation Variance:</Text>
            <Text style={{ fontWeight: "bold", color: "#22543D" }}>{CurrencyFormatter(threeWayReconciliation.variance)}</Text>
          </View>
          <Text style={styles.badgeSuccess}>IN BALANCE / RECONCILED</Text>
        </View>
      </View>

      {/* Client Trust Liability Schedule */}
      <Text style={styles.sectionTitle}>
        3. {liabilityTitle.toUpperCase()} (AS OF {periodEndFormatted.toUpperCase()})
      </Text>
      <View style={styles.table}>
        <View style={styles.tableHeaderRow}>
          <Text style={{ ...styles.thText, width: "12%" }}>Deal #</Text>
          <Text style={{ ...styles.thText, width: "42%" }}>Property Address / Reference</Text>
          <Text style={{ ...styles.thText, width: "26%" }}>Client / Parties</Text>
          <Text style={{ ...styles.thText, width: "20%", ...styles.textRight }}>Trust Funds Held</Text>
        </View>

        {trustLiabilityList.length > 0 ? (
          trustLiabilityList.map((item, idx) => {
            const heldAmt = accountData.accountType === "real_estate_trust" ? item.reTrustBalance : item.commTrustBalance;
            return (
              <View key={`liability_row_${idx}`} style={styles.tableRow}>
                <Text style={{ ...styles.tdTextBold, width: "12%" }}>{item.dealNo}</Text>
                <Text style={{ ...styles.tdText, width: "42%" }}>{item.address}</Text>
                <Text style={{ ...styles.tdText, width: "26%" }}>{item.clientName}</Text>
                <Text style={{ ...styles.tdTextBold, width: "20%", ...styles.textRight }}>
                  {CurrencyFormatter(heldAmt)}
                </Text>
              </View>
            );
          })
        ) : (
          <View style={styles.tableRow}>
            <Text style={{ ...styles.tdText, width: "100%", ...styles.textCenter, color: "#718096" }}>
              No open trust funds held for this account at month-end.
            </Text>
          </View>
        )}

        <View style={styles.tableTotalRow}>
          <Text style={{ ...styles.thText, width: "80%" }}>
            TOTAL {accountTitle.toUpperCase()} LIABILITY (RECONCILED TO BOOK BALANCE)
          </Text>
          <Text style={{ ...styles.thText, width: "20%", ...styles.textRight }}>
            {CurrencyFormatter(threeWayReconciliation.totalTrustLiability)}
          </Text>
        </View>
      </View>

      {/* Broker of Record Signature Area */}
      <View style={styles.signatureSection} wrap={false}>
        <Text style={{ ...styles.sectionTitle, marginTop: 0 }}>
          4. BROKER OF RECORD CERTIFICATION & SIGNATURE (O. REG. 579/05, S. 13)
        </Text>
        <Text style={styles.certText}>
          "In accordance with Section 13 of Ontario Regulation 579/05 under the Trust in Real Estate Services Act, 2002 (TRESA), I, Navnit Badwal, Broker of Record for Dwelly Realty Inc., Brokerage, hereby certify that I have reviewed this Trust Account Reconciliation Statement and all supporting cash journals, client ledgers, and bank records for the {accountTitle} for the month ended {periodEndFormatted}. I confirm that the trust account is in balance, maintained separately, and compliant with all statutory trust requirements."
        </Text>

        <View style={styles.signatureGrid}>
          <View style={{ width: "65%" }}>
            <View style={styles.sigLine} />
            <Text style={styles.sigLabel}>Signature of Broker of Record</Text>
            <Text style={styles.sigSub}>Navnit Badwal — Broker of Record, Dwelly Realty Inc.</Text>
          </View>
          <View style={{ width: "30%" }}>
            <View style={styles.sigLine} />
            <Text style={styles.sigLabel}>Date Signed</Text>
            <Text style={styles.sigSub}>YYYY-MM-DD</Text>
          </View>
        </View>

        <Text style={styles.footerNotice}>
          Statutory Notice: Under Section 13 & 14 of O. Reg. 579/05, this trust account reconciliation statement and supporting records must be retained for at least six (6) years.
        </Text>
      </View>
    </Page>
  );
};

const MonthlyTrustReconciliationPDF = ({ reconciliationData, accountFilter = "all" }) => {
  if (!reconciliationData) {
    return (
      <Document>
        <Page size="LETTER" style={styles.page}>
          <Text>No reconciliation data available.</Text>
        </Page>
      </Document>
    );
  }

  const {
    periodLabel,
    periodEndFormatted,
    dueDateFormatted,
    reTrust,
    commTrust,
    combined,
    monthlyJournal,
  } = reconciliationData;

  const showReTrust = accountFilter === "all" || accountFilter === "real_estate_trust";
  const showCommTrust = accountFilter === "all" || accountFilter === "commission_trust";

  return (
    <Document>
      {/* 1. Real Estate Trust Account Page */}
      {showReTrust && (
        <TrustAccountPage
          accountData={reTrust}
          periodLabel={periodLabel}
          periodEndFormatted={periodEndFormatted}
          dueDateFormatted={dueDateFormatted}
          accountTitle="Real Estate Trust Account"
          liabilityTitle="Client Deposit Liability Schedule (Funds Held in Trust)"
          liabilityHeaderCol="Deposit Liability Listing:"
        />
      )}

      {/* 2. Commission Trust Account Page */}
      {showCommTrust && (
        <TrustAccountPage
          accountData={commTrust}
          periodLabel={periodLabel}
          periodEndFormatted={periodEndFormatted}
          dueDateFormatted={dueDateFormatted}
          accountTitle="Commission Trust Account"
          liabilityTitle="Commission Trust Liability Schedule (Funds Held in Trust)"
          liabilityHeaderCol="Commission Trust Listing:"
        />
      )}

      {/* 3. Consolidated Summary & Activity Journal Page (Included when All Accounts selected) */}
      {accountFilter === "all" && (
        <Page size="LETTER" style={styles.page}>
          <View style={styles.headerRow}>
            <View style={{ width: "45%" }}>
              <Image style={styles.logo} src={Logo} />
              <Text style={{ ...styles.companyInfo, fontWeight: "bold" }}>
                Dwelly Realty Inc., Brokerage
              </Text>
              <Text style={styles.companyInfo}>
                Consolidated Trust Accounting & Activity Journal
              </Text>
            </View>
            <View style={styles.titleBlock}>
              <Text style={styles.accountBadge}>CONSOLIDATED SUMMARY</Text>
              <Text style={styles.docTitle}>TRUST ACCOUNTS COMBINED</Text>
              <Text style={styles.metaText}>Period Ended: {periodEndFormatted}</Text>
            </View>
          </View>

          {/* Consolidated Trust Accounts Comparison */}
          <Text style={styles.sectionTitle}>
            CONSOLIDATED SUMMARY OF ALL TRUST ACCOUNTS (S. 13)
          </Text>
          <View style={styles.table}>
            <View style={styles.tableHeaderRow}>
              <Text style={{ ...styles.thText, width: "32%" }}>Account</Text>
              <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>Opening Balance</Text>
              <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>Debits (Receipts)</Text>
              <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>Credits (Disb.)</Text>
              <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>Closing Balance</Text>
            </View>

            <View style={styles.tableRow}>
              <Text style={{ ...styles.tdTextBold, width: "32%" }}>Real Estate Trust Account</Text>
              <Text style={{ ...styles.tdText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(reTrust.openingBalance)}</Text>
              <Text style={{ ...styles.tdText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(reTrust.totalDebits)}</Text>
              <Text style={{ ...styles.tdText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(reTrust.totalCredits)}</Text>
              <Text style={{ ...styles.tdTextBold, width: "17%", ...styles.textRight }}>{CurrencyFormatter(reTrust.closingBalance)}</Text>
            </View>

            <View style={styles.tableRow}>
              <Text style={{ ...styles.tdTextBold, width: "32%" }}>Commission Trust Account</Text>
              <Text style={{ ...styles.tdText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(commTrust.openingBalance)}</Text>
              <Text style={{ ...styles.tdText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(commTrust.totalDebits)}</Text>
              <Text style={{ ...styles.tdText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(commTrust.totalCredits)}</Text>
              <Text style={{ ...styles.tdTextBold, width: "17%", ...styles.textRight }}>{CurrencyFormatter(commTrust.closingBalance)}</Text>
            </View>

            <View style={styles.tableTotalRow}>
              <Text style={{ ...styles.thText, width: "32%" }}>TOTAL ALL TRUST FUNDS</Text>
              <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(combined.openingBalance)}</Text>
              <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(combined.totalDebits)}</Text>
              <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(combined.totalCredits)}</Text>
              <Text style={{ ...styles.thText, width: "17%", ...styles.textRight }}>{CurrencyFormatter(combined.closingBalance)}</Text>
            </View>
          </View>

          {/* Monthly Trust Activity Journal */}
          <Text style={styles.sectionTitle}>
            MONTHLY TRUST ACTIVITY JOURNAL ({periodLabel})
          </Text>
          <View style={styles.table}>
            <View style={styles.tableHeaderRow}>
              <Text style={{ ...styles.thText, width: "13%" }}>Date</Text>
              <Text style={{ ...styles.thText, width: "20%" }}>Account</Text>
              <Text style={{ ...styles.thText, width: "33%" }}>Details / Payee / Payer</Text>
              <Text style={{ ...styles.thText, width: "18%" }}>Deal / Ref</Text>
              <Text style={{ ...styles.thText, width: "16%", ...styles.textRight }}>Amount</Text>
            </View>

            {monthlyJournal.length > 0 ? (
              monthlyJournal.map((t, idx) => (
                <View
                  key={`cons_txn_${idx}`}
                  style={{
                    ...styles.tableRow,
                    backgroundColor: idx % 2 === 1 ? "#F7FAFC" : "#FFFFFF",
                  }}
                >
                  <Text style={{ ...styles.tdText, width: "13%" }}>
                    {t.date ? moment(t.date).format("ll") : "-"}
                  </Text>
                  <Text style={{ ...styles.tdText, width: "20%" }}>
                    {t.account === "real_estate_trust"
                      ? "Real Estate Trust"
                      : t.account === "commission_trust"
                      ? "Commission Trust"
                      : t.account}
                  </Text>
                  <Text style={{ ...styles.tdText, width: "33%" }}>
                    {t.details || "-"}
                  </Text>
                  <Text style={{ ...styles.tdText, width: "18%" }}>
                    {t.deal_no ? `Deal #${t.deal_no}` : t.reference || "-"}
                  </Text>
                  <Text
                    style={{
                      ...styles.tdTextBold,
                      width: "16%",
                      ...styles.textRight,
                      color: t.type === "credit" ? "#C53030" : "#22543D",
                    }}
                  >
                    {t.type === "credit"
                      ? `(${FormatCurrencyPlain(t.amount)})`
                      : FormatCurrencyPlain(t.amount)}
                  </Text>
                </View>
              ))
            ) : (
              <View style={styles.tableRow}>
                <Text style={{ ...styles.tdText, width: "100%", ...styles.textCenter, color: "#718096" }}>
                  No trust transactions recorded during this month.
                </Text>
              </View>
            )}

            <View style={styles.tableTotalRow}>
              <Text style={{ ...styles.thText, width: "84%" }}>
                TOTAL MONTHLY NET ACTIVITY (RECEIPTS LESS DISBURSEMENTS)
              </Text>
              <Text style={{ ...styles.thText, width: "16%", ...styles.textRight }}>
                {CurrencyFormatter(combined.netMonthlyActivity !== undefined ? combined.netMonthlyActivity : round2(combined.closingBalance - combined.openingBalance))}
              </Text>
            </View>
          </View>

          {/* Consolidated Broker of Record Sign-off */}
          <View style={styles.signatureSection} wrap={false}>
            <View style={styles.signatureGrid}>
              <View style={{ width: "65%" }}>
                <View style={styles.sigLine} />
                <Text style={styles.sigLabel}>Broker of Record Verification</Text>
                <Text style={styles.sigSub}>Navnit Badwal — Dwelly Realty Inc., Brokerage</Text>
              </View>
              <View style={{ width: "30%" }}>
                <View style={styles.sigLine} />
                <Text style={styles.sigLabel}>Verification Date</Text>
                <Text style={styles.sigSub}>YYYY-MM-DD</Text>
              </View>
            </View>
          </View>
        </Page>
      )}
    </Document>
  );
};

export default MonthlyTrustReconciliationPDF;
